'use client';

import { RouterName } from "@/lib/enum/RouterEnum";
import { useContentSize } from "@/components/SizeProvider";
import DraggableWindow from "@/components/DraggableWindow";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLenis } from "lenis/react";
import VideoPlayer from "@/components/VideoPlayer";
import styles from './home-content.module.scss';
import { useZIndexManager } from "@/lib/hooks/useZIndexManager";
import { useRouter } from "next/navigation";
import { ProjectModel } from "@/lib/models/Project";

// Notes:
// - For infinite scrolling to work seamlessly, 
//   - The first draggable window needs to be completely offscreen.
//   - There needs to be a gap of window.innerHeight between the bottom of the last element and the top of the first element.
// - In order to show the first draggable window on page load, we need to adjust the vertical scroll value accordingly when this page is navigated to.
// - To preserve the relative positions of the windows as intended by the designers, we compute initial positions of windows based on a referenceContentSize.
interface Props {
  projects: ProjectModel[];
}

export default function HomeContent({ projects }: Props) {
  const { width: widthContent, height: heightContent } = useContentSize();
  const lenis = useLenis();
  const homeContent = useRef<HTMLDivElement>(null);
  const { zIndexMap, handleElementClick } = useZIndexManager({ wrapAround: 50 });
  const router = useRouter();
  const [isCollapsing, setIsCollapsing] = useState(false);
  const pendingNavigationRef = useRef<string | null>(null);

  const draggableWindowWidth = Math.ceil(widthContent * 0.234);
  let draggableWindowHeight = Math.ceil(draggableWindowWidth * 0.5616); // Our thumbnails/videos are usually 2400 x 1404, so we derive the height as width * (1404/2400)
  const titleFontSize = Math.ceil(widthContent / 160);
  draggableWindowHeight += titleFontSize + 24; // title text height + title padding (13px + 11px)
  const draggableWindowSize = { width: draggableWindowWidth, height: draggableWindowHeight };

  const videoPlayerWidth = Math.ceil(widthContent * 0.5);
  let videoPlayerHeight = Math.ceil(videoPlayerWidth * 0.5625); // Showreel is 1920 x 1080, so we derive height as width * (1080/1920)
  videoPlayerHeight += 24; // 24px is vertical padding, excluding the height of the text
  const videoPlayerSize = { width: videoPlayerWidth, height: videoPlayerHeight };

  // Since our desktop aspect ratio is 18 / 9, on a FHD display, the size would be 1920 x 960.
  // Define window preset positions
  const windowPositioningData = {
    // Window positions will cycle through this set of positions
    // Note: x position is based off a reference widthContent of 1920px, whereas y position represents the vertical offset from the first draggable window, based on a reference content height of 960px.
    presets: [
      { x: 437, y: 0 },
      { x: 1025, y: 20 },
      { x: 733, y: 258 },
      { x: 970, y: 530 },
      { x: 442, y: 604 },
      { x: 927, y: 864 },
    ],
    // Spacing between each set of preset positions
    verticalSpacing: 1200,
  };

  const getInitialPosition = (index: number) => {
    const presetPosition = windowPositioningData.presets[index % windowPositioningData.presets.length];
    const presetCycle = Math.floor(index / windowPositioningData.presets.length); // Cycle 0 = first set of presets -> no need to add vertical spacing.

    const leftOffset = (window.innerWidth - widthContent) / 2;
    const positionX = presetPosition.x / 1920 * widthContent + leftOffset;
    const offsetY = (presetPosition.y + presetCycle * windowPositioningData.verticalSpacing) / 960 * heightContent;
    const topOffset = (window.innerHeight - heightContent) / 2;
    const positionY = topOffset + heightContent * 0.85 + offsetY;

    return {
      x: positionX,
      y: positionY
    };
  };

  const presetPositions = useMemo<{ x: number; y: number }[] | null>(() => {
    if (widthContent <= 0 || heightContent <= 0) return null;
    return projects.map((_, index) => getInitialPosition(index));
  }, [widthContent, heightContent, projects.length]);

  const showreelPosition = useMemo<{ x: number; y: number } | null>(() => {
    if (widthContent <= 0 || heightContent <= 0) return null;
    return {
      x: window.innerWidth * 0.50 - videoPlayerSize.width / 2,
      y: (window.innerHeight - heightContent) / 2 + heightContent * 0.15,
    };
  }, [widthContent, heightContent, videoPlayerSize.width]);

  // Adjust homeContent minHeight
  useEffect(() => {
    if (widthContent === 0 || heightContent === 0 || !homeContent.current) {
      return;
    }

    const lastIndex = projects.length - 1;
    const lastPosition = getInitialPosition(lastIndex);

    const height = lastPosition.y + draggableWindowSize.height + window.innerHeight * 0.2;
    homeContent.current.style.height = `${height}px`;

    lenis?.resize();
  }, [widthContent, heightContent, homeContent.current, projects.length, draggableWindowSize.height]);

  // Handle navigation after collapse animation completes
  useEffect(() => {
    if (isCollapsing && pendingNavigationRef.current) {
      const timer = setTimeout(() => {
        router.push(pendingNavigationRef.current!);
        pendingNavigationRef.current = null;
      }, 300); // Make sure this value matches the collapse animation duration for DraggableWindow, VideoPlayer and ProjectInfo.

      return () => clearTimeout(timer);
    }
  }, [isCollapsing, router]);

  const collapseAndNavigate = (href: string) => {
    setIsCollapsing(true);
    pendingNavigationRef.current = href;
  };

  function getProjectPath(title: string) {
    let charExits = /\W/g;
    const path = title.replaceAll(" ", "_").replace(charExits, '');
    return `${RouterName.projectDetail}/${path}`;
  }

  // Assumes draggable's pivot point is at top left
  const constrainDraggableToViewport = (pos: { x: number; y: number }, size: { width: number; height: number }) => {
    const contentHeight = homeContent.current?.clientHeight ?? document.documentElement.clientHeight;
    const constrainedX = Math.max(-size.width / 2, Math.min(pos.x, window.innerWidth - size.width / 2));
    const constrainedY = Math.min(Math.max(-size.height / 2, pos.y), contentHeight - size.height / 2);
    return { x: constrainedX, y: constrainedY };
  };

  return (
    <div className={styles['home-content']} ref={homeContent}>
      {projects
        .map((project, index) => {
          const id = `project-${index}`;
          return (
            <DraggableWindow
              key={index}
              title={`${(project.order + 1).toString().padStart(2, '0')}\\${project.detailTitle.replaceAll(' ', '_')}`}
              src={project.detailImages[0]}
              initialPosition={presetPositions?.[index] ?? { x: 0, y: 0 }}
              size={draggableWindowSize}
              allowDragging={true}
              constrainingFunction={constrainDraggableToViewport}
              href={getProjectPath(project.title)}
              zIndex={zIndexMap[id] || 0}
              onPointerDownCapture={() => handleElementClick(id)}
              isOnLandingPage={false}
              widthContent={widthContent}
              isCollapsing={isCollapsing}
              onNavigate={collapseAndNavigate}
              autoplay
              eager={index === 0}
            />
          );
        })
      }
      <VideoPlayer
        title="THE_OTHER_LAB_SHOWREEL_2025"
        src="/assets/video/Showreel_fixed.mp4"
        initialPosition={showreelPosition ?? { x: 0, y: 0 }}
        size={videoPlayerSize}
        allowDragging={true}
        constrainingFunction={constrainDraggableToViewport}
        zIndex={zIndexMap['showreel'] || 0}
        onPointerDownCapture={() => handleElementClick('showreel')}
        isOnLandingPage={false}
        widthContent={widthContent}
        isCollapsing={isCollapsing}
        eager={false}
        autoplayOnIdle
        poster="/assets/image/BG.png"
      />
    </div>
  )
}
